import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import {
  CHART,
  CHART_MARGIN,
  TIME_SYNC_ID,
  Y_AXIS_WIDTH,
  formatDayLong,
} from '../../config/chartTheme';
import type { TimeRow } from './timeRows';

/**
 * Datenabdeckung je Tag: wie viele Inserate hinter dem Wert darüber stehen.
 *
 * Das ist die Antwort auf ein reales Problem statt eines Schwellenwerts. Für
 * Mai 2026 meldet die Auswertung 100 % Belegung — rechnerisch korrekt, aber
 * getragen von einem einzigen Inserat (der erste Snapshot stammt vom
 * 31.05.2026). Solche Tage auszublenden wäre eine willkürliche Grenze;
 * stattdessen wird die Datenbasis sichtbar gemacht und die Einordnung bleibt
 * beim Betrachter.
 *
 * Als recharts-Diagramm statt als CSS-Streifen: bei Tagesauflösung wären das
 * ~2 200 DOM-Knoten mit Hover-Flächen von unter einem Pixel. So teilt der
 * Streifen zudem Achse, Tooltip und Zoom mit den Diagrammen darüber — die
 * Ausrichtung ergibt sich aus der Achse, statt von Hand nachgerechnet zu werden.
 */
export default function CoverageStrip({
  rows,
  total,
  source = 'selection',
  height = 56,
}: {
  rows: TimeRow[];
  /** Bezugsgröße: Inserate in der aktuellen Auswahl. */
  total: number;
  source?: 'selection' | 'reference';
  height?: number;
}) {
  if (rows.length === 0) return null;
  const key = source === 'reference' ? 'referenceN' : 'n';

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={rows} margin={{ ...CHART_MARGIN, top: 2 }} syncId={TIME_SYNC_ID}>
          {/* Achse nur zur Ausrichtung — beschriftet wird oben. */}
          <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']} hide />
          <YAxis domain={[0, Math.max(total, 1)]} width={Y_AXIS_WIDTH} hide />
          <Tooltip content={<CoverageTooltip total={total} dataKey={key} />} />
          <Area
            type="step"
            dataKey={key}
            stroke={CHART.reference}
            strokeWidth={1}
            fill={CHART.reference}
            fillOpacity={0.55}
            isAnimationActive={false}
            activeDot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
      <p className="text-xs text-gray-400 mt-1">
        Datenabdeckung je Tag (max. {total.toLocaleString('de-DE')}). Wo die Fläche einbricht,
        stützt sich der Wert oben auf wenige Inserate und ist nicht repräsentativ.
      </p>
    </div>
  );
}

function CoverageTooltip({
  active,
  payload,
  total,
  dataKey,
}: {
  active?: boolean;
  payload?: { payload: TimeRow }[];
  total: number;
  dataKey: 'n' | 'referenceN';
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const n = row[dataKey];
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-xs">
      <div className="font-semibold text-gray-700">{formatDayLong(row.iso)}</div>
      <div className="mt-1 text-gray-500">
        {n == null ? 'Keine Angabe der Plattform' : `${n} von ${total} Inseraten`}
      </div>
    </div>
  );
}
